import React, { useMemo, memo } from 'react';
import { useIntl } from 'react-intl';
import ReactEcharts from 'echarts-for-react';
import numeral from 'numeral';
import TrendsLogo from 'assets/images/trends-logo.png';
import { sum } from 'lodash';
import XLogo from 'assets/svgs/twitter-inverted-logo.svg';
import FBLogo from 'assets/svgs/fb-circle-logo.svg';
import InstaLogo from 'assets/svgs/insta-inverted-logo.svg';
import MiscLogo from 'assets/svgs/rss.svg';

interface EvolutionChartProps {
  XAxis: string[];
  title: string;
  legend: string[];
  data: Array<{
    name: 'Facebook' | 'X' | 'Instagram' | 'Misc' | 'Total';
    value: number[] | undefined;
  }>;
}

const EvolutionChart: React.FC<EvolutionChartProps> = ({
  data,
  title,
  legend,
  XAxis,
}) => {
  const intl = useIntl();

  const translatedLegend = legend.map(item =>
    intl.formatMessage({ id: `results.service.${item}` }),
  );

  const iconMap = {
    Facebook: FBLogo,
    X: XLogo,
    Instagram: InstaLogo,
    Misc: MiscLogo,
    Total: null,
  };

  const iconGraphics = data.slice(0, 3).map((item, index) => ({
    type: 'image',
    style: {
      image: iconMap[item.name],
      width: 20,
      height: 20,
    },
    right: 70 + index * 14,
    top: 10,
    z: data.length - index,
  }));

  const colors: {
    [key in 'Facebook' | 'X' | 'Instagram' | 'Misc' | 'Total']: string[];
  } = useMemo(
    () => ({
      Facebook: ['#C6D1E8', '#1877f2'],
      Instagram: ['#F0C0DE', '#CD2E92'],
      X: ['#CCCCCC', '#000000'],
      Misc: ['#fd9226', '#fd9226'],
      Total: ['#9a9a9a', '#444444'],
    }),
    [],
  );

  const magicTypeTranslations = useMemo(
    () => ({
      line: intl.formatMessage({ id: 'toolbox.line' }),
      bar: intl.formatMessage({ id: 'toolbox.bar' }),
    }),
    [intl],
  );
  const totalPosts = useMemo(() => {
    return new Intl.NumberFormat('pt-BR').format(
      data.reduce((acc, current) => {
        return acc + sum(current.value);
      }, 0),
    );
  }, [data]);

  return (
    <ReactEcharts
      ref={e => {
        return e?.getEchartsInstance().setOption({
          baseOption: {
            title: {
              show: true,
              text: title
                ? `${title} - ${totalPosts} ${intl.formatMessage({
                    id: 'results.representativeness.collected',
                  })}`
                : '',
              textStyle: {
                fontSize: 16,
              },
            },
            textStyle: {
              fontFamily: 'Lato',
            },
            grid: {
              top: 42,
              left: 60,
              right: 20,
              bottom: 130,
            },
            legend: {
              data: translatedLegend,
              bottom: 40,
              left: 50,
            },
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow',
              },
            },
            graphic: iconGraphics,
            toolbox: {
              show: true,
              bottom: 70,
              right: 10,
              feature: {
                magicType: {
                  show: true,
                  type: ['line', 'bar'],
                  title: magicTypeTranslations,
                  option: {
                    bar: {
                      stack: 'social',
                    },
                    line: {
                      stack: false,
                    },
                  },
                },
                saveAsImage: {
                  show: true,
                  title: intl.formatMessage({ id: 'toolbox.save' }),
                  name: 'term_evolution_buzzmonitor',
                  excludeComponents: ['toolbox', 'dataZoom'],
                },
              },
            },
            brush: {
              xAxisIndex: 'all',
              brushLink: 'all',
              outOfBrush: {
                colorAlpha: 0.1,
              },
            },
            dataZoom: [{ type: 'slider', backgroundColor: '#fff' }],
            xAxis: {
              data: XAxis,
              type: 'category',
              boundaryGap: false,
              axisLine: {
                show: false,
              },
            },
            yAxis: {
              type: 'value',
              axisLine: {
                show: false,
              },
              axisLabel: {
                formatter: (value: number) => numeral(value).format('0.[0]a'),
              },
            },
            series: data.map(item => ({
              data: item.value,
              name: intl.formatMessage({ id: `results.service.${item.name}` }),
              type: 'line',
              stack: false,
              smooth: true,
              symbolSize: 6,
              lineStyle: {
                color: colors[item.name][1],
                width: 2,
              },
              areaStyle: {
                color: colors[item.name][0],
                opacity: 0.4,
              },
              itemStyle: { color: colors[item.name][1] },
            })),
          },
          media: [
            {
              query: { maxWidth: 500 },
              option: {
                toolbox: {
                  show: false,
                },
                legend: {
                  textStyle: {
                    fontSize: 10,
                  },
                },
                graphic: {},
              },
            },
          ],
        });
      }}
      option={{}}
      style={{ height: '400px', width: '100%' }}
    />
  );
};

const comparisonFn = (
  prevProps: EvolutionChartProps,
  newProps: EvolutionChartProps,
): boolean => {
  return JSON.stringify(prevProps) === JSON.stringify(newProps);
};

export default memo(EvolutionChart, comparisonFn);
